/**
 * HTTP-клиент Nest-панели — второй бэк, стоящий рядом с основным API
 * (`VITE_NEST_API_URL`, например `https://panel.showgo.kz/api`).
 *
 * Почему не переиспользуем `http()`:
 *   • другой хост и другой префикс — `/v1/office` там не существует;
 *   • нативный camelCase (`ageLimit`, `updatedAt`). Snake-конвертер из
 *     `http.ts` отправил бы в PATCH `age_limit`, и бэк ответил бы
 *     «Неизвестный блок "age_limit"»;
 *   • нет legacy-конверта `{result, status, payload}` — тело ответа
 *     отдаётся как есть;
 *   • нет авторизации и `Country-Code`: инстанс чекера сам привязан к
 *     стране (COUNTRY_CODE на его стороне), лишние заголовки только
 *     ломали бы preflight.
 *
 * Ошибки приводим к тому же `HttpError`, что и основной клиент, —
 * `extractErrorMessage` уже умеет доставать `message` из тела (у Nest
 * он бывает и строкой, и массивом строк валидации).
 */

import axios, {type AxiosError, type AxiosInstance, type AxiosRequestConfig} from 'axios';

import {env} from './env';
import {HttpError} from './http.types';

/**
 * Панель поднята не во всех инстансах — ключ пустой значит раздел
 * настроек просто не показываем.
 */
export const isNestApiEnabled = env.VITE_NEST_API_URL.trim() !== '';

const client: AxiosInstance = axios.create({
  baseURL: env.VITE_NEST_API_URL.trim().replace(/\/$/, ''),
  withCredentials: false,
  headers: {Accept: 'application/json'},
});

client.interceptors.response.use(
  (res) => res,
  (err: AxiosError) =>
    Promise.reject(
      new HttpError(
        err.response?.statusText || err.message || 'HTTP error',
        err.response?.status ?? 0,
        err.response?.data,
      ),
    ),
);

export type NestHttpOptions = Omit<AxiosRequestConfig, 'url' | 'baseURL' | 'data'> & {
  /** Тело запроса. Уходит как есть — без конверсии ключей. */
  body?: unknown;
};

/**
 * Фетчер всех ручек панели.
 *
 *   const schema = await nestHttp<BlocksSchemaResponse>('blocks/schema');
 *   await nestHttp('sessions/resolve', {method: 'POST', body: {url}});
 *
 * Успехом считается любой 2xx (`resolve` отвечает 201, не 200).
 */
export const nestHttp = async <T = unknown>(
  path: string,
  options: NestHttpOptions = {},
): Promise<T> => {
  if (!isNestApiEnabled) {
    throw new HttpError('VITE_NEST_API_URL не задан', 0, null);
  }

  const {body, ...rest} = options;

  const res = await client.request<T>({
    url: path.startsWith('/') ? path.slice(1) : path,
    method: rest.method ?? (body !== undefined ? 'POST' : 'GET'),
    data: body,
    ...rest,
  });

  return res.data;
};
